import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import { VoucherRecord, AnalyticsMetrics, AIInsight, ProcessingResult } from '../types/voucher'

interface FilterState {
    productName: string | null
    voucherStatus: string | null
    quarter: string | null
    registrationStatus: string | null
    datasetTag?: string | null
}

interface DashboardState {
    // Navigation state
    activeView: string
    setActiveView: (view: string) => void

    // Data state
    vouchers: VoucherRecord[]
    analytics: AnalyticsMetrics | null
    insights: AIInsight[]

    // Loading states
    isLoadingVouchers: boolean
    isLoadingAnalytics: boolean
    isLoadingInsights: boolean

    // Error states
    vouchersError: string | null
    analyticsError: string | null
    insightsError: string | null

    // Filter state
    filters: FilterState
    setFilters: (filters: Partial<FilterState>) => void
    clearFilters: () => void

    // Upload state
    uploadResult: ProcessingResult | null
    uploadError: string | null

    // Actions
    setVouchers: (vouchers: VoucherRecord[]) => void
    setAnalytics: (analytics: AnalyticsMetrics) => void
    setInsights: (insights: AIInsight[]) => void

    setLoadingVouchers: (loading: boolean) => void
    setLoadingAnalytics: (loading: boolean) => void
    setLoadingInsights: (loading: boolean) => void

    setVouchersError: (error: string | null) => void
    setAnalyticsError: (error: string | null) => void
    setInsightsError: (error: string | null) => void

    setUploadResult: (result: ProcessingResult | null) => void
    setUploadError: (error: string | null) => void

    // Reset functions
    resetErrors: () => void
    resetData: () => void
}

const initialFilters: FilterState = {
    productName: null,
    voucherStatus: null,
    quarter: null,
    registrationStatus: null,
    datasetTag: null
}

export const useDashboardStore = create<DashboardState>()(
    devtools(
        (set) => ({
            // Initial state
            activeView: 'overview',
            vouchers: [],
            analytics: null,
            insights: [],

            isLoadingVouchers: false,
            isLoadingAnalytics: false,
            isLoadingInsights: false,

            vouchersError: null,
            analyticsError: null,
            insightsError: null,

            filters: initialFilters,

            uploadResult: null,
            uploadError: null,

            // Actions
            setActiveView: (view) => set({ activeView: view }),

            setVouchers: (vouchers) => set({ vouchers }),
            setAnalytics: (analytics) => set({ analytics }),
            setInsights: (insights) => set({ insights }),

            setLoadingVouchers: (loading) => set({ isLoadingVouchers: loading }),
            setLoadingAnalytics: (loading) => set({ isLoadingAnalytics: loading }),
            setLoadingInsights: (loading) => set({ isLoadingInsights: loading }),

            setVouchersError: (error) => set({ vouchersError: error }),
            setAnalyticsError: (error) => set({ analyticsError: error }),
            setInsightsError: (error) => set({ insightsError: error }),

            setUploadResult: (result) => set({ uploadResult: result }),
            setUploadError: (error) => set({ uploadError: error }),

            setFilters: (newFilters) =>
                set((state) => ({
                    filters: { ...state.filters, ...newFilters }
                })),

            clearFilters: () => set({ filters: initialFilters }),

            resetErrors: () => set({
                vouchersError: null,
                analyticsError: null,
                insightsError: null,
                uploadError: null
            }),

            resetData: () => set({
                vouchers: [],
                analytics: null,
                insights: [],
                uploadResult: null
            })
        }),
        {
            name: 'dashboard-store'
        }
    )
)